import { compareOrganisationNames } from "@/utils/organisationSort";
// ============= Full file contents =============

import { useState, useEffect } from 'react';
import { useQuery } from "@tanstack/react-query";
import { Plus, Search } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";

interface AddAssignmentSectionProps {
  onAssignmentAdded: () => void;
  prefilledDeviceId?: string;
  prefilledDeviceName?: string;
}

// Natural sort: alphabetical, with numbers compared numerically (SSL2 before SSL10)
const naturalCompare = (a: string, b: string) =>
  a.localeCompare(b, undefined, { numeric: true, sensitivity: 'base' });

export const AddAssignmentSection = ({ 
  onAssignmentAdded, 
  prefilledDeviceId,
  prefilledDeviceName 
}: AddAssignmentSectionProps) => {
  const [showForm, setShowForm] = useState(false);
  const [newAssignment, setNewAssignment] = useState({
    deviceId: prefilledDeviceId || "",
    userId: "",
    organizationId: "",
  });
  const [deviceSearchTerm, setDeviceSearchTerm] = useState("");
  const [deviceSelectOpen, setDeviceSelectOpen] = useState(false);
  const [orgSearchTerm, setOrgSearchTerm] = useState("");
  const [orgSelectOpen, setOrgSelectOpen] = useState(false);
  const [userSearchTerm, setUserSearchTerm] = useState("");
  const [userSelectOpen, setUserSelectOpen] = useState(false);

  useEffect(() => {
    if (prefilledDeviceId) {
      setShowForm(true);
      setNewAssignment(prev => ({ ...prev, deviceId: prefilledDeviceId }));
    }
  }, [prefilledDeviceId]);

  const { data: devices } = useQuery({
    queryKey: ["devices"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("device_details")
        .select("device_id, device_name, device_serial");
      
      if (error) throw error;
      return data;
    },
  });

  const { data: users } = useQuery({
    queryKey: ["users"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("profiles")
        .select("id, username, user_id");
      
      if (error) throw error;
      return data;
    },
  });

  const { data: userRoles } = useQuery({
    queryKey: ["userRoles"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("user_roles")
        .select("user_id, role, organization_id");
      
      if (error) throw error;
      return data;
    },
  });

  const { data: organizations } = useQuery({
    queryKey: ["organizations"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("organizations")
        .select("id, name");
      
      if (error) throw error;
      return data;
    },
  });

  // Get users for the selected organization with their details
  const organizationUsers = userRoles?.filter(role => 
    newAssignment.organizationId === "" || 
    role.organization_id === newAssignment.organizationId
  ).map(role => {
    const user = users?.find(u => u.user_id === role.user_id);
    return {
      ...role,
      username: user?.username,
      profileId: user?.id
    };
  }) || [];

  // Get organization options with user counts
  const organizationOptions = organizations?.map(org => {
    const userCount = userRoles?.filter(role => role.organization_id === org.id).length || 0;
    return {
      ...org,
      userCount
    };
  }) || [];

  // Sort organizations alphabetically (natural order)
  const sortedOrganizations = [...organizationOptions].sort((a, b) =>
    compareOrganisationNames(a.name || "", b.name || "")
  );

  // Filter + sort organizations by search term (by name)
  const filteredOrganizations = sortedOrganizations.filter(org =>
    (org.name || "").toLowerCase().includes(orgSearchTerm.toLowerCase())
  );

  // Sort users alphabetically by username (fallback to user ID)
  const sortedOrganizationUsers = [...organizationUsers].sort((a, b) =>
    naturalCompare(a.username || a.user_id, b.username || b.user_id)
  );

  // Filter + sort users by search term (by username or user ID)
  const userSearchLower = userSearchTerm.toLowerCase();
  const filteredUsers = sortedOrganizationUsers.filter(userRole =>
    (userRole.username || "").toLowerCase().includes(userSearchLower) ||
    userRole.user_id.toLowerCase().includes(userSearchLower)
  );

  // Sort devices by name (fallback to serial, then device ID)
  const sortedDevices = [...(devices || [])].sort((a, b) =>
    naturalCompare(
      a.device_name || a.device_serial || a.device_id,
      b.device_name || b.device_serial || b.device_id
    )
  );

  // Filter devices based on search term (by name, serial, or device_id)
  const filteredDevices = sortedDevices.filter(device => {
    const searchLower = deviceSearchTerm.toLowerCase();
    const nameMatch = device.device_name?.toLowerCase().includes(searchLower);
    const idMatch = device.device_id.toLowerCase().includes(searchLower);
    const serialMatch = device.device_serial?.toLowerCase().includes(searchLower);
    return nameMatch || idMatch || serialMatch;
  });

  const resetSearches = () => {
    setOrgSearchTerm("");
    setUserSearchTerm("");
    setDeviceSearchTerm("");
  };

  const handleAddAssignment = async () => {
    if (!newAssignment.deviceId || !newAssignment.userId) {
      toast.error("Please select a device and user");
      return;
    }

    try {
      const { error } = await supabase.from("device_assignments").insert([
        {
          device_id: newAssignment.deviceId,
          user_id: newAssignment.userId,
          assigned_at: new Date().toISOString(),
        },
      ]);

      if (error) throw error;

      toast.success("Assignment added successfully!");
      setNewAssignment({ deviceId: "", userId: "", organizationId: "" });
      resetSearches();
      setShowForm(false);
      onAssignmentAdded();
    } catch (error) {
      console.error("Error adding assignment:", error);
      toast.error("Failed to add assignment");
    }
  };

  const handleCancel = () => {
    setShowForm(false);
    setNewAssignment({ deviceId: "", userId: "", organizationId: "" });
    resetSearches();
  };

  return (
    <div className="add-assignment-section">
      {!showForm ? (
        <Button 
          onClick={() => setShowForm(true)}
          className="w-full md:w-auto bg-[#DFEF97] md:border md:border-black text-black hover:bg-[#DFEF97]/90"
        >
          <Plus className="h-4 w-4 mr-2" />
          Add Assignment
        </Button>
      ) : (
        <div className="bg-card rounded-lg border shadow-sm p-6 space-y-6">
          <div className="flex justify-between items-center">
            <div>
              <h3 className="text-lg font-semibold">Add New Assignment</h3>
              {prefilledDeviceName && (
                <p className="text-sm text-muted-foreground mt-1">
                  Device: <span className="font-medium">{prefilledDeviceName}</span>
                </p>
              )}
            </div>
            <Button 
              variant="ghost" 
              className="md:border-0 border border-border"
              onClick={handleCancel}
            >
              Cancel
            </Button>
          </div>

          <div className="grid gap-4 max-w-md">
            <div className="grid gap-2">
              <label className="text-sm font-medium">1. Select Organization</label>
              <Select
                value={newAssignment.organizationId}
                open={orgSelectOpen}
                onOpenChange={(open) => {
                  setOrgSelectOpen(open);
                  if (!open) setOrgSearchTerm("");
                }}
                onValueChange={(value) => {
                  setNewAssignment(prev => ({ 
                    ...prev, 
                    organizationId: value,
                    userId: "" // Reset user when organization changes
                  }));
                  setUserSearchTerm("");
                  setOrgSearchTerm("");
                  setOrgSelectOpen(false);
                }}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Choose organization" />
                </SelectTrigger>
                <SelectContent className="max-h-[300px]">
                  <div className="sticky top-0 z-10 bg-background border-b p-2">
                    <div className="relative">
                      <Search className="absolute left-2 top-1/2 transform -translate-y-1/2 text-muted-foreground h-4 w-4" />
                      <Input
                        placeholder="Search organizations..."
                        value={orgSearchTerm}
                        onChange={(e) => setOrgSearchTerm(e.target.value)}
                        className="pl-8 h-8"
                      />
                    </div>
                  </div>
                  <div className="max-h-[200px] overflow-y-auto">
                    {filteredOrganizations.length > 0 ? (
                      filteredOrganizations.map((org) => (
                        <SelectItem key={org.id} value={org.id}>
                          {org.name} ({org.userCount} users)
                        </SelectItem>
                      ))
                    ) : (
                      <div className="p-2 text-center text-muted-foreground text-sm">
                        No organizations found
                      </div>
                    )}
                  </div>
                </SelectContent>
              </Select>
            </div>

            <div className="grid gap-2">
              <label className="text-sm font-medium">2. Select User</label>
              <Select
                value={newAssignment.userId}
                open={userSelectOpen}
                onOpenChange={(open) => {
                  setUserSelectOpen(open);
                  if (!open) setUserSearchTerm("");
                }}
                onValueChange={(value) => {
                  setNewAssignment(prev => ({ ...prev, userId: value }));
                  setUserSearchTerm("");
                  setUserSelectOpen(false);
                }}
                disabled={!newAssignment.organizationId}
              >
                <SelectTrigger>
                  <SelectValue placeholder={
                    !newAssignment.organizationId 
                      ? "Select organization first" 
                      : "Choose user"
                  } />
                </SelectTrigger>
                <SelectContent className="max-h-[300px]">
                  <div className="sticky top-0 z-10 bg-background border-b p-2">
                    <div className="relative">
                      <Search className="absolute left-2 top-1/2 transform -translate-y-1/2 text-muted-foreground h-4 w-4" />
                      <Input
                        placeholder="Search users..."
                        value={userSearchTerm}
                        onChange={(e) => setUserSearchTerm(e.target.value)}
                        className="pl-8 h-8"
                      />
                    </div>
                  </div>
                  <div className="max-h-[200px] overflow-y-auto">
                    {filteredUsers.length > 0 ? (
                      filteredUsers.map((userRole) => (
                        <SelectItem key={userRole.user_id} value={userRole.user_id}>
                          {userRole.username || userRole.user_id} 
                          {userRole.role === 'admin' && ' (Admin)'}
                        </SelectItem>
                      ))
                    ) : (
                      <div className="p-2 text-center text-muted-foreground text-sm">
                        No users found
                      </div>
                    )}
                  </div>
                </SelectContent>
              </Select>
            </div>

            {!prefilledDeviceId && (
              <div className="grid gap-2">
                <label className="text-sm font-medium">3. Select Device</label>
                <Select
                  value={newAssignment.deviceId}
                  open={deviceSelectOpen}
                  onOpenChange={(open) => {
                    setDeviceSelectOpen(open);
                    if (!open) setDeviceSearchTerm("");
                  }}
                  onValueChange={(value) => {
                    setNewAssignment(prev => ({ ...prev, deviceId: value }));
                    setDeviceSearchTerm("");
                    setDeviceSelectOpen(false);
                  }}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Choose device" />
                  </SelectTrigger>
                  <SelectContent className="max-h-[300px]">
                    <div className="sticky top-0 z-10 bg-background border-b p-2">
                      <div className="relative">
                        <Search className="absolute left-2 top-1/2 transform -translate-y-1/2 text-muted-foreground h-4 w-4" />
                        <Input
                          placeholder="Search by name or serial..."
                          value={deviceSearchTerm}
                          onChange={(e) => setDeviceSearchTerm(e.target.value)}
                          className="pl-8 h-8"
                        />
                      </div>
                    </div>
                    <div className="max-h-[200px] overflow-y-auto">
                      {filteredDevices.length > 0 ? (
                        filteredDevices.map((device) => (
                          <SelectItem key={device.device_id} value={device.device_id}>
                            {device.device_name || device.device_serial || device.device_id} ({device.device_serial || device.device_id.slice(0, 8)})
                          </SelectItem>
                        ))
                      ) : (
                        <div className="p-2 text-center text-muted-foreground text-sm">
                          No devices found
                        </div>
                      )}
                    </div>
                  </SelectContent>
                </Select>
              </div>
            )}

            <Button 
              onClick={handleAddAssignment} 
              className="w-full mt-4 bg-[#DFEF97] text-black hover:bg-[#DFEF97]/90"
              disabled={!newAssignment.deviceId || !newAssignment.userId}
            >
              Add Assignment
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};
